<template>
  <div>
    <h1>Meeting Overview</h1>
    <asset-overview v-if="ready"
      asset-icon="fa-headset"
      :assets="sortedMeetings"
      :kable-headers="kableHeaders"
      :kable-rows="kableRows"
      :show-archived-assets="showArchived"
      @tablecallback="tableCallback"
      @archive-toggle="updateArchiveToggle"
    >
      <template #new-asset>
        <add-meetings @meeting-added="getMeetings"></add-meetings>
      </template>
    </asset-overview>
  </div>
</template>

<script>
import PageReadyMixin from '@mixins/page-ready-mixin';
import getIcon from '@utils/kate-table-icons';
import { sortObjectArray } from '@utils/sort-by-object-property';
import AssetOverview from '../asset-overview.vue';
import AddMeetings from './add-meeting.vue';
import ArchiveToastConfirmMixin from '../archive-toast-confirm-mixin';

export default {
  components: {
    AssetOverview,
    AddMeetings,
  },

  mixins: [PageReadyMixin, ArchiveToastConfirmMixin],

  data() {
    return {
      meetingsReady: false,
      meetings: [],
      meetingToUpdate: undefined,
      showArchived: false,
    };
  },

  beforeMount() {
    this.getMeetings();
  },

  computed: {
    ready() {
      return this.meetingsReady;
    },
    sortedMeetings() {
      return sortObjectArray(this.meetings.slice(), 'name', false);
    },

    kableHeaders() {
      return {
        edit: {
          name: 'Edit',
          type: 'url',
        },
        title: {
          name: 'Title',
          filterable: true,
          sortable: true,
          type: 'url',
        },
        description: {
          name: 'Description',
          type: 'string',
          options: {
            displayLength: 80,
          },
        },
        supportFunction: {
          name: 'Support Function',
          sortable: true,
          type: 'pill',
          options: {
            tooltip: true,
            tooltipText: 'The support role of the intended meeting owner, e.g. coach, data mentor',
            type: supportFunction => (supportFunction === 'coach' ? 'primary' : 'neutral'),
          },
        },
        displayTags: {
          name: 'Tags',
          type: 'tags',
          filterable: true,
          sortable: true,
        },
        type: {
          name: 'Type',
          type: 'pill',
          sortable: true,
          options: {
            tooltip: true,
            tooltipText: 'Whether the meeting is intended to be scheduled on an ad-hoc basis only, i.e. not part of the standard programme meetings.',
            type: () => 'warning',
          },
        },
        employer_visibility: {
          name: 'Visibility',
          type: 'pill',
          sortable: true,
          options: {
            tooltip: true,
            tooltipText: this.employerVisibilityTooltipText,
            type: status => {
              const statusMap = {
                visible: 'warning',
                hidden: 'danger',
              };
              return statusMap[status];
            },
          },
        },
        archive: {
          name: 'Archive',
          type: 'button',
          sortable: true,
          options: {
            preset: row => (row.is_active ? 'archive' : 'unarchive'),
            title: row => (row.is_active ? `Archive ${row.name || 'meeting'}` : `Unarchive ${row.name || 'meeting'}`),
            ariaLabel: row => (row.is_active ? `Archive ${row.name || 'meeting'}` : `Unarchive ${row.name || 'meeting'}`),
          },
        },
        delete: {
          name: 'Delete',
          type: 'button',
          options: {
            preset: 'delete-unstyled',
            title: row => `Delete ${row.name || 'meeting'}`,
            ariaLabel: row => `Delete ${row.name || 'meeting'}`,
          },
        },
      };
    },

    kableRows() {
      return this.sortedMeetings.map((val, index) => ({
        edit: {
          key: index,
          text: getIcon({ icon: 'edit' }),
          path: {
            name: 'edit_meeting',
            params: {
              meetingId: val.id,
            },
          },
        },
        title: {
          key: index,
          text: val.name,
          path: {
            name: 'curriculum_asset_details',
            params: {
              assetId: val.id,
              assetType: 'meeting',
            },
          },
        },
        description: val.description,
        employer_visibility: val.share_with_employer_default ? 'visible' : 'hidden',
        supportFunction: val.support_function,
        type: val.is_adhoc ? 'Adhoc' : null,
        displayTags: {
          tags: val.tags || [],
          sortBy: val.tags ? [...val.tags].sort().join(' ') : '',
        },
        ...val,
      })).filter(mt => (this.showArchived || mt.is_active));
    },
    employerVisibilityTooltipText() {
      return `Whether instances of this meeting are visible to the learner's employer by default. 
This can be overridden for a particular instance of the meeting by the meeting owner.`;
    },
  },

  methods: {
    closeEditModal() {
      this.meetingToUpdate = undefined;
    },

    updateArchiveToggle(val) {
      this.showArchived = val;
    },

    getMeetings() {
      this.$logger.info('Getting meetings');
      this.$Loading.start();
      this.meetingsReady = false;
      this.$http.get('/api/curriculum/admin/meeting')
        .then(result => {
          this.meetings = result.data.meetings;
        }).catch(err => {
          if (err.response && err.response.status !== 404) {
            this.$logger.error('Error getting meetings', undefined, err);
            this.$ktoast.showError(err);
          } else {
            this.$logger.warn('No meetings found');
            this.meetings = [];
          }
        }).then(() => {
          this.meetingsReady = true;
          this.$Loading.finish();
        });
    },

    deleteConfirmationCallback(meetingId) {
      this.$logger.info('Deleting meetings', { meetingId }, true);
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/admin/meeting/${meetingId}`)
        .then(() => {
          this.$logger.info('Successfuly deleted meeting', { meetingId });
          this.$ktoast.success('Meeting have been deleted');
          this.getMeetings();
        }).catch(err => {
          this.$logger.autowarn('Error deleting meeting', { meetingId }, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    deleteMeetings(meetingId) {
      this.$ktoast.confirmToast('You are about to delete this meeting.', 'warning', () => {
        this.deleteConfirmationCallback(meetingId);
      });
    },

    tableCallback(key, index, row, header) {
      if (header === 'archive') {
        this.tableArchiveCallback(row);
      }
      if (header === 'delete') {
        this.deleteMeetings(row.id);
      }
      if (header === 'edit') {
        const mt = this.meetings.find(x => x.id === row.id);
        if (mt && typeof mt === 'object') {
          this.meetingToUpdate = JSON.parse(JSON.stringify(mt));
        } else {
          this.meetingToUpdate = undefined;
        }
      }
    },

    archiveCallback(mId) {
      this.$logger.info('Archiving meeting', { mId }, true);
      this.$Loading.start();
      this.$http.put(`/api/curriculum/admin/meeting/${mId}/archive`)
        .then(() => {
          this.$logger.info('Successfully archived meeting', { mId });
          this.$ktoast.success('Successfully archived meeting');
          this.getMeetings();
        }).catch(err => {
          this.$logger.error('Error archiving meeting', { mId }, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    unarchiveCallback(mId) {
      this.$logger.info('Unarchiving meeting', { mId }, true);
      this.$Loading.start();
      this.$http.put(`/api/curriculum/admin/meeting/${mId}/unarchive`)
        .then(() => {
          this.$logger.info('Successfully unarchived meeting', { mId });
          this.$ktoast.success('Meeting has been unarchived');
          this.getMeetings();
        }).catch(err => {
          this.$logger.error('Error unarchiving meeting', { mId }, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.$Loading.finish();
        });
    },
  },
};
</script>
